/** UI-level task actions: mutation + toast with Undo where it helps. */
import { toast } from 'sonner';
import {
  formatFireAt,
  SNOOZE_LABEL,
  type Priority,
  type SnoozeOption,
  type Task,
} from '@karya/core';
import { db } from '@/db/dexie';
import * as m from './mutations';

function completedToast(task: Task, prevStatus: string, recurring: boolean): void {
  toast.success(recurring ? 'Completed. The next one will be created.' : 'Completed', {
    description: task.name,
    action: { label: 'Undo', onClick: () => void m.setTaskStatus(task.id, prevStatus) },
  });
}

export async function completeWithToast(task: Task): Promise<void> {
  const prevStatus = task.status_id;
  const res = await m.toggleComplete(task);
  if (res.completed) completedToast(task, prevStatus, res.recurring);
}

/** Status change from a status menu; moving to a done status offers Undo like completing does. */
export async function setStatusWithToast(task: Task, statusId: string): Promise<void> {
  const prevStatus = task.status_id;
  const res = await m.setTaskStatus(task.id, statusId);
  if (res.completed) completedToast(task, prevStatus, res.recurring);
}

export async function deleteWithToast(task: Task): Promise<void> {
  const ids = await m.deleteTask(task.id);
  toast('Moved to Trash', {
    description: task.name,
    action: { label: 'Undo', onClick: () => void m.restoreTasks(ids) },
  });
}

export async function snoozeWithToast(task: Task, option: SnoozeOption): Promise<void> {
  const until = await m.snoozeTask(task.id, option);
  const p = await db().profiles.toCollection().first();
  const tz = p?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const label =
    typeof option === 'object' ? 'Snoozed' : `Snoozed ${SNOOZE_LABEL[option].toLowerCase()}`;
  toast(label, { description: `Reminder at ${formatFireAt(until.toISOString(), tz)}` });
}

export async function setPriorityWithToast(task: Task, p: Priority | null): Promise<void> {
  await m.setPriority(task.id, p);
}

export async function copyTaskLink(taskId: string): Promise<void> {
  const url = `${window.location.origin}/t/${taskId}`;
  try {
    await navigator.clipboard.writeText(url);
    toast('Link copied');
  } catch {
    toast(url);
  }
}

export async function duplicateWithToast(task: Task): Promise<void> {
  const copy = await m.duplicateTask(task.id);
  if (copy) toast('Duplicated', { description: copy.name });
}
